'use client';

/**
 * VideoAnalyzer — watch the actual funnel video inside Beast, see where
 * viewers fall off, and read/write annotated advice at those points.
 *
 * Timeline model (v1, aggregate analytics):
 *  - green→red gradient zone between avg watch time and avg stop position =
 *    the measured drop-off zone
 *  - a red marker at avg stop position = the drop-off point
 *  - notes (AI or manual) render as pins; clicking one seeks the video
 * When the stats API ships a per-second retention endpoint this upgrades to
 * a full retention curve with every dip flagged.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Loader2, Sparkles, MessageSquarePlus, Trash2, TrendingDown, Bot, User, MapPin, Send,
} from 'lucide-react';
import { useSessionChat } from '@/components/chat/useSessionChat';
import { MarkdownBlock } from '@/components/chat/Markdown';
import { ToolBlock } from '@/components/chat/ToolBlock';

export interface VideoStats {
  videoPage: string;
  videoId: string;
  totalViews: number;
  uniqueViewers: number;
  avgCompletion: number;     // %
  completionRate: number;    // %
  avgDuration: number;       // s watched
  avgStopPosition: number;   // s position where avg viewer stops
}

interface VideoNote {
  id: number;
  timestamp_s: number;
  kind: 'manual' | 'ai' | 'dropoff';
  comment: string;
  advice: string;
}

/** Minimal markdown for AI notes: **bold** → <strong>, keep everything else as-is. */
function renderNote(text: string): React.ReactNode {
  return text.split(/\*\*/).map((chunk, i) =>
    i % 2 === 1 ? <strong key={i} className="text-white font-semibold">{chunk}</strong> : chunk,
  );
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function VideoAnalyzer({ funnelId, video, hlsOrigin, onClose }: {
  funnelId: string;
  video: VideoStats;
  hlsOrigin: string;          // e.g. https://yevlakhova.com
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [notes, setNotes] = useState<VideoNote[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedNote, setSelectedNote] = useState<number | null>(null);

  // AI assistant — a real gateway agent session (Fable), same machinery as
  // every other assistant in Beast. Slug is per funnel+video.
  const chatSlug = `video-asst-${funnelId}-${video.videoId}`;
  const {
    messages: chat, streaming: chatBusy, streamText, streamParts,
    send: sendToAgent, historyHydrated: chatHydrated,
  } = useSessionChat(chatSlug);
  const [chatReady, setChatReady] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat.length, chatBusy, streamText]);

  // open (or wake) the dedicated session with cwd ~/beast
  useEffect(() => {
    let cancelled = false;
    setChatReady(false);
    fetch('/api/gw/sessions/open', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: chatSlug, path: '.', name: `Video · ${video.videoId}`, icon: '🎬' }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) { if (!d.ok) throw new Error(d.error); setChatReady(true); } })
      .catch(e => { if (!cancelled) toast.error(e instanceof Error ? e.message : 'Assistant session failed'); });
    return () => { cancelled = true; };
  }, [chatSlug, video.videoId]);

  const hlsUrl = `${hlsOrigin}/hls/${video.videoId}/master.m3u8`;

  // hls.js player
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    let hls: any;
    (async () => {
      if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = hlsUrl; // Safari native
      } else {
        const Hls = (await import('hls.js')).default;
        if (Hls.isSupported()) {
          hls = new Hls();
          hls.loadSource(hlsUrl);
          hls.attachMedia(el);
        }
      }
    })();
    const onMeta = () => setDuration(el.duration || 0);
    const onTime = () => setCurrent(el.currentTime || 0);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('timeupdate', onTime);
    return () => {
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('timeupdate', onTime);
      if (hls) hls.destroy();
    };
  }, [hlsUrl]);

  const loadNotes = useCallback(() => {
    fetch(`/api/funnels/${funnelId}/videos/${video.videoId}/notes`)
      .then(r => r.json())
      .then(d => setNotes(d.notes || []))
      .catch(() => {});
  }, [funnelId, video.videoId]);
  useEffect(() => { loadNotes(); }, [loadNotes]);

  const seek = (s: number) => {
    const el = videoRef.current;
    if (el) { el.currentTime = s; el.play().catch(() => {}); }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    toast.info('AI is watching the drop-off segment — ~1-2 minutes…');
    try {
      const res = await fetch(`/api/funnels/${funnelId}/videos/${video.videoId}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dropoffAt: video.avgStopPosition,
          avgDuration: video.avgDuration,
          completionRate: video.completionRate,
          hlsUrl,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      toast.success('Drop-off analyzed — advice pinned on the timeline');
      loadNotes();
      setSelectedNote(d.note?.id ?? null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Analysis failed', { duration: 10000 });
    } finally { setAnalyzing(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await fetch(`/api/funnels/${funnelId}/videos/${video.videoId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timestamp_s: current, comment: noteText.trim() }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setNoteText(''); setAddingNote(false);
      loadNotes();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to add note');
    }
  };

  const removeNote = async (id: number) => {
    await fetch(`/api/funnels/${funnelId}/videos/${video.videoId}/notes?noteId=${id}`, { method: 'DELETE' });
    loadNotes();
  };

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text || !chatReady || chatBusy) return;
    setChatInput('');
    const notesCtx = notes.map(n => `- [${fmtTime(n.timestamp_s)}, ${n.kind}] ${n.comment.slice(0, 300)}`).join('\n');
    const grounding = chat.length === 0
      ? `You are the video assistant inside Beast's funnel analyzer. The user is watching the sales video "${video.videoId}" (in Russian) from funnel "${funnelId}" and wants to discuss its actual content and how to improve retention.\n` +
        `VIDEO: HLS stream at ${hlsUrl} (duration ~${Math.round(dur)}s). To SEE/HEAR any moment, clip it yourself, e.g.:\n` +
        `  ffmpeg -y -ss <start> -i "${hlsUrl}" -t 60 -vf "fps=1/5,scale=512:-2" /tmp/va-frame-%02d.jpg  (then Read the frames)\n` +
        `  ffmpeg -y -ss <start> -i "${hlsUrl}" -t 60 -vn -ar 16000 /tmp/va-audio.wav  (for speech, e.g. whisper)\n` +
        `ANALYTICS: ${video.totalViews} views, avg watch ${fmtTime(video.avgDuration)}, avg viewer stops at ${fmtTime(video.avgStopPosition)}, ${video.completionRate}% finish.\n` +
        (notesCtx ? `TIMELINE NOTES:\n${notesCtx}\n` : '') +
        `Answer in the user's language (native Russian — живой, без калек). Reference timestamps as M:SS. Keep answers tight — small chat panel.`
      : undefined;
    sendToAgent(`[playhead ${fmtTime(current)}] ${text}`, undefined, grounding);
  };

  const dur = duration || Math.max(video.avgStopPosition * 1.2, 1);
  const pct = (s: number) => `${Math.min((s / dur) * 100, 100)}%`;

  return (
    <div className="rounded-xl border border-cyan-500/25 bg-white/[0.05] p-4">
      {/* header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-red-400" />
          <h3 className="text-sm font-semibold text-white">
            Analyzing: {video.videoId} <span className="text-gray-600">on /{video.videoPage}</span>
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runAnalysis} disabled={analyzing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 text-xs font-medium transition-colors disabled:opacity-50"
          >
            {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {analyzing ? 'Watching…' : 'AI: analyze drop-off'}
          </button>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        {/* player + timeline */}
        <div>
          <video ref={videoRef} controls className="w-full rounded-lg bg-black aspect-video" />

          {/* retention timeline */}
          <div className="mt-3">
            <div className="relative h-9 rounded bg-white/5 overflow-visible cursor-pointer"
              onClick={e => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                seek(((e.clientX - r.left) / r.width) * dur);
              }}
            >
              {/* healthy zone */}
              <div className="absolute inset-y-0 left-0 rounded-l bg-green-500/20" style={{ width: pct(video.avgDuration) }} />
              {/* drop-off zone: avg watch time → avg stop position */}
              <div className="absolute inset-y-0 bg-gradient-to-r from-amber-500/30 to-red-500/40"
                style={{ left: pct(video.avgDuration), width: `calc(${pct(video.avgStopPosition)} - ${pct(video.avgDuration)})` }} />
              {/* drop-off marker */}
              <div className="absolute inset-y-0 w-0.5 bg-red-500" style={{ left: pct(video.avgStopPosition) }}>
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-red-400 whitespace-nowrap font-semibold">
                  ▼ drop-off {fmtTime(video.avgStopPosition)}
                </span>
              </div>
              {/* playhead */}
              <div className="absolute inset-y-0 w-0.5 bg-cyan-400" style={{ left: pct(current) }} />
              {/* note pins */}
              {notes.map(n => (
                <button key={n.id}
                  onClick={e => { e.stopPropagation(); seek(n.timestamp_s); setSelectedNote(n.id); }}
                  className="absolute -bottom-2 -translate-x-1/2 text-purple-400 hover:text-purple-200 hover:scale-125 transition-transform"
                  style={{ left: pct(n.timestamp_s) }}
                  title={`${fmtTime(n.timestamp_s)} — ${n.comment.slice(0, 80)}`}
                >
                  <MapPin className="w-4 h-4" fill="currentColor" />
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-gray-600 mt-3">
              <span>0:00</span>
              <span className="text-green-400">avg watch {fmtTime(video.avgDuration)}</span>
              <span className="text-red-400">avg stop {fmtTime(video.avgStopPosition)}</span>
              <span>{fmtTime(dur)}</span>
            </div>
          </div>

          {/* quick stats */}
          <div className="flex gap-4 mt-2 text-[11px] text-gray-500">
            <span>{video.totalViews} views</span>
            <span>{video.uniqueViewers} unique</span>
            <span>{video.avgCompletion}% avg completion</span>
            <span className="text-red-400">{video.completionRate}% finish</span>
          </div>
        </div>

        {/* right column: markers (always visible) + assistant chat below */}
        <div className="flex flex-col gap-2 h-[560px]">
          {/* markers — top half, always visible */}
          <div className="flex-1 min-h-0 flex flex-col gap-2 overflow-y-auto">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-400">Markers & advice</p>
            <button
              onClick={() => setAddingNote(v => !v)}
              className="flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" /> at {fmtTime(current)}
            </button>
          </div>

          {addingNote && (
            <div className="rounded-lg border border-cyan-500/30 p-2 space-y-2">
              <textarea
                value={noteText} onChange={e => setNoteText(e.target.value)}
                placeholder={`Comment at ${fmtTime(current)}…`}
                className="w-full bg-transparent text-xs text-white placeholder-gray-600 focus:outline-none resize-none h-14"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setAddingNote(false)} className="text-[11px] text-gray-500 hover:text-white">Cancel</button>
                <button onClick={addNote} className="text-[11px] px-2 py-1 rounded bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25">Save</button>
              </div>
            </div>
          )}

          {notes.length === 0 && !addingNote && (
            <p className="text-[11px] text-gray-600 leading-relaxed">
              No markers yet. Hit <span className="text-purple-300">AI: analyze drop-off</span> — the AI watches the
              segment where viewers quit and pins what's wrong + how to fix it. Or add your own comment at any point.
            </p>
          )}

          {notes.map(n => (
            <button key={n.id}
              onClick={() => { seek(n.timestamp_s); setSelectedNote(n.id); }}
              className={`text-left rounded-lg border p-2.5 transition-colors ${
                selectedNote === n.id ? 'border-purple-500/50 bg-purple-500/10' : 'border-white/[0.08] hover:border-gray-700'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                {n.kind === 'ai' ? <Bot className="w-3 h-3 text-purple-400" /> : <User className="w-3 h-3 text-cyan-400" />}
                <span className="text-[10px] font-mono text-gray-500">{fmtTime(n.timestamp_s)}</span>
                <span className={`text-[9px] uppercase px-1 rounded ${n.kind === 'ai' ? 'bg-purple-500/15 text-purple-300' : 'bg-cyan-500/15 text-cyan-300'}`}>{n.kind}</span>
                <span className="flex-1" />
                <span onClick={e => { e.stopPropagation(); removeNote(n.id); }}
                  className="text-gray-700 hover:text-red-400 cursor-pointer"><Trash2 className="w-3 h-3" /></span>
              </div>
              <p className="text-[11px] text-gray-300 whitespace-pre-wrap leading-relaxed">{renderNote(n.comment)}</p>
              {n.advice && (
                <p className="text-[11px] text-green-300/90 whitespace-pre-wrap leading-relaxed mt-1.5 border-t border-white/[0.04] pt-1.5">
                  💡 {renderNote(n.advice)}
                </p>
              )}
            </button>
          ))}
          </div>

          {/* assistant chat — below the markers, transcript grows above the input */}
          <div className="border-t border-white/[0.04] pt-2 flex flex-col gap-2">
            <p className="text-xs font-semibold text-gray-400 flex items-center gap-1.5">
              <Bot className="w-3.5 h-3.5 text-cyan-400" /> Assistant — спроси про это видео
            </p>
            {(chat.length > 0 || chatBusy) && chatHydrated && (
              <div className="max-h-[280px] overflow-y-auto space-y-2 pr-1">
                {chat.map((m) => (
                  <div key={m.id} className={`rounded-lg p-2.5 text-[11px] leading-relaxed ${
                    m.role === 'user' ? 'bg-cyan-500/10 text-cyan-100 ml-4 whitespace-pre-wrap' : 'bg-white/5 text-gray-300 mr-1'
                  }`}>
                    {m.role === 'user'
                      ? m.content.replace(/^\[playhead \d+:\d+\] /, '')
                      : (m.parts && m.parts.length > 0
                          ? m.parts.map((p, i) => p.type === 'text'
                              ? <MarkdownBlock key={i} text={p.content} />
                              : p.type === 'thinking'
                              ? <div key={i} className="text-xs text-gray-500 italic whitespace-pre-wrap">{p.content}</div>
                              : <ToolBlock key={p.tool.id || i} name={p.tool.name} input={p.tool.input} output={p.tool.output} status={p.tool.status} />)
                          : <MarkdownBlock text={m.content} />)}
                  </div>
                ))}
                {chatBusy && (
                  <div className="rounded-lg p-2.5 text-[11px] leading-relaxed bg-white/5 text-gray-300 mr-1">
                    {streamParts.map((p, i) => p.type === 'text'
                      ? <MarkdownBlock key={i} text={p.content} />
                      : p.type === 'thinking'
                      ? <div key={i} className="text-xs text-gray-500 italic whitespace-pre-wrap">{p.content}</div>
                      : <ToolBlock key={p.tool.id || i} name={p.tool.name} input={p.tool.input} output={p.tool.output} status={p.tool.status} />)}
                    {streamText && <MarkdownBlock text={streamText} />}
                    <span className="flex items-center gap-2 text-gray-500 mt-1">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" /> работает…
                    </span>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
            {/* input — 3 rows */}
            <div className="rounded-lg border border-white/[0.08] focus-within:border-cyan-500/50 transition-colors">
                <textarea
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  placeholder={`Вопрос про видео на ${fmtTime(current)}… (Enter — отправить, Shift+Enter — новая строка)`}
                  rows={3}
                  className="w-full bg-transparent text-xs text-white placeholder-gray-600 focus:outline-none resize-none p-2"
                />
                <div className="flex justify-end p-1.5 pt-0">
                  <button onClick={sendChat} disabled={!chatReady || chatBusy || !chatInput.trim()}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 text-[11px] disabled:opacity-40 transition-colors">
                    <Send className="w-3 h-3" /> Send
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
